import { useState, useMemo, useRef, useEffect } from 'react'
import { Toggle } from '../components/Toggle'
import { TrashIcon } from '../components/icons/TrashIcon'
import { PencilIcon } from '../components/icons/PencilIcon'
import { MCPIcon } from '../components/icons/MCPIcon'
import { LinkIcon } from '../components/icons/LinkIcon'
import { RefreshIcon } from '../components/icons/RefreshIcon'
import { ChevronRightIcon } from '../components/icons/ChevronRightIcon'
import { WarningIcon } from '../components/icons/WarningIcon'
import { HeartbeatIcon } from '../components/icons/HeartbeatIcon'
import { LightningIcon } from '../components/icons/LightningIcon'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch, ApiError } from '../lib/api'
import type {
  Connection,
  ConnectionHealth,
  ConnectionOperation,
  ConnectionListResponse,
  ConnectionCreate,
  ConnectionUpdate,
  ConnectionBindingRef,
  DiscoverRequest,
  DiscoverResponse,
  DiscoveryPattern,
  ConnectionAuthMethod,
} from '../lib/types'
import { Spinner } from '../components/Spinner'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Card } from '../components/Card'
import { useEntityEditorRegistration } from '../hooks/useEntityEditorRegistration'
import { NotSubscribedCard } from './PreviewPage'

/** Strip trailing period and ensure sentence case for display. */
function normalizeName(name: string): string {
  const s = name.trimEnd().replace(/\.$/, '').trimEnd()
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1)
}

/* ─── Shared styles ─── */

const labelStyle: React.CSSProperties = {
  display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: 'var(--text-primary)',
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6,
  fontSize: 13, fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--bg-input)',
  outline: 'none', boxSizing: 'border-box',
}

const hintStyle: React.CSSProperties = { fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }

const btnGhostStyle: React.CSSProperties = {
  padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
  cursor: 'pointer', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)',
}

const btnPrimaryStyle: React.CSSProperties = {
  padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
  cursor: 'pointer', background: 'var(--accent)', color: 'var(--text-inverse)', border: '1px solid var(--accent)',
  display: 'inline-flex', alignItems: 'center', gap: 6,
}

/* ─── Tool tags (discovery pattern) ─── */

function ToolTags({ pattern }: { pattern: string }) {
  const tags = pattern === 'SearchInvoke' ? ['search', 'invoke'] : ['standard']
  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
      {tags.map(t => (
        <span key={t} style={{
          fontSize: 10, fontWeight: 500, padding: '1px 7px', borderRadius: 8,
          background: 'var(--bg-badge)', color: 'var(--text-tertiary)',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}>{t}</span>
      ))}
    </div>
  )
}

/* ─── Pulse dot ─── */

function PulseDot({ state }: { state: 'idle' | 'checking' | 'healthy' | 'unreachable' }) {
  const colors: Record<string, string> = {
    idle: 'var(--text-tertiary)',
    checking: 'var(--warning)',
    healthy: 'var(--accent)',
    unreachable: 'var(--danger)',
  }
  return (
    <span style={{
      width: 7, height: 7, borderRadius: '50%',
      background: colors[state] ?? 'var(--text-tertiary)',
      display: 'inline-block', flexShrink: 0,
    }} />
  )
}

/* ─── MCP error messages ─── */

const MCP_ERROR_KEYS = {
  'mcp.authRejected': 'globalConnectionsTab.mcpErrors.authRejected',
  'mcp.unreachable': 'globalConnectionsTab.mcpErrors.unreachable',
  'mcp.timeout': 'globalConnectionsTab.mcpErrors.timeout',
} as const

/* ─── Editor op helper ─── */

interface EditorOperation extends ConnectionOperation {
  _key: string
}

interface EditorForm {
  name: string
  description: string
  endpointUrl: string
  authMethod: ConnectionAuthMethod
  oauthAuthorizationUrl: string
  oauthTokenUrl: string
  oauthClientId: string
  oauthClientSecret: string
}

const emptyEditorForm: EditorForm = {
  name: '', description: '', endpointUrl: '', authMethod: 'None',
  oauthAuthorizationUrl: '', oauthTokenUrl: '', oauthClientId: '', oauthClientSecret: '',
}

function buildEditorForm(conn: Connection): EditorForm {
  return {
    name: conn.name,
    description: conn.description ?? '',
    endpointUrl: conn.endpointUrl,
    authMethod: conn.authMethod,
    oauthAuthorizationUrl: (conn.authConfig?.authorizationUrl as string) ?? '',
    oauthTokenUrl: (conn.authConfig?.tokenUrl as string) ?? '',
    oauthClientId: (conn.authConfig?.clientId as string) ?? '',
    oauthClientSecret: '',
  }
}

function buildBinding(conn: Connection): ConnectionBindingRef {
  return {
    connectionId: conn.id,
    isEnabled: conn.isEnabled,
    operations: conn.operations.filter(op => op.id).map(op => ({
      operationId: op.id!,
      isEnabled: op.isEnabled,
    })),
  }
}

/* ─── Preview card (MS-598 AC8/AC9) ───
   Rendered instead of the full config UI below while FEATURES.globalConnectionsConfig
   is false. The legacy UI is retained (hide-not-delete) for when Custom Connections ships.
   Shares the same not-subscribed-state card/copy as the other MVP Preview surfaces
   (Marketplace, Workflows, Agents Analytics) — see PreviewPage.tsx. */

export function ConnectionsPreview() {
  const { t } = useTranslation()
  return (
    <NotSubscribedCard
      cardTitle={t('globalConnectionsTab.preview.cardTitle')}
      description={t('globalConnectionsTab.preview.description')}
      voteFeature="Custom Connections"
    />
  )
}

/* ─── Props ─── */

export interface GlobalConnectionsTabProps {
  connectionBindings: ConnectionBindingRef[]
  savedBindings: ConnectionBindingRef[]
  onConnectionToggle: (connectionId: string, isEnabled: boolean) => void
  onOperationToggle: (connectionId: string, operationId: string, isEnabled: boolean) => void
  onAdded: (binding: ConnectionBindingRef) => void
  onRemoved: (connectionId: string) => void
}

/* ─── GlobalConnectionsTab ─── */

export function GlobalConnectionsTab({
  connectionBindings,
  savedBindings,
  onConnectionToggle,
  onAdded,
  onRemoved,
}: GlobalConnectionsTabProps) {
  const { t } = useTranslation()
  const apiFetch = useApiFetch()
  const queryClient = useQueryClient()

  const [editorMode, setEditorMode] = useState<'idle' | 'create' | 'edit'>('idle')
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null)
  const [editorForm, setEditorForm] = useState<EditorForm>(emptyEditorForm)
  const [editorOps, setEditorOps] = useState<EditorOperation[]>([])
  const [discoveryPattern, setDiscoveryPattern] = useState<DiscoveryPattern>('StandardTools')
  /** Snapshot of {form, ops, pattern} taken when the editor opens — dirty detection
   *  built from scratch, no existing tracking here previously (unlike GlobalSkillsTab). */
  const [editorSnapshot, setEditorSnapshot] = useState<{ form: EditorForm; ops: EditorOperation[]; pattern: DiscoveryPattern } | null>(null)
  const [oauthAdvancedOpen, setOauthAdvancedOpen] = useState(false)
  const [isDiscovering, setIsDiscovering] = useState(false)
  const [discoverError, setDiscoverError] = useState<string | null>(null)
  const [editorError, setEditorError] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<Connection | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteDependents, setDeleteDependents] = useState<{ agentId: string; agentDisplayName: string }[] | null>(null)

  const [disableConnConfirm, setDisableConnConfirm] = useState<Connection | null>(null)
  const [saveConnConfirm, setSaveConnConfirm] = useState(false)
  const [healthState, setHealthState] = useState<Record<string, 'idle' | 'checking' | 'healthy' | 'unreachable'>>({})
  const [healthResult, setHealthResult] = useState<Record<string, ConnectionHealth>>({})
  const [discoveryToast, setDiscoveryToast] = useState<string | null>(null)
  const diffCountsRef = useRef({ added: 0, deprecated: 0 })

  const connectionsQuery = useQuery({
    queryKey: ['connections', 'global'],
    queryFn: () => apiFetch<ConnectionListResponse>('/api/v1/connections'),
  })

  const connections = connectionsQuery.data?.items ?? []

  useEffect(() => {
    const items = connectionsQuery.data?.items
    if (!items) return
    setHealthState(prev => {
      const next: Record<string, 'idle' | 'checking' | 'healthy' | 'unreachable'> = { ...prev }
      for (const c of items) {
        if (!(c.id in next) && c.lastHealthStatus) {
          next[c.id] = c.lastHealthStatus
        }
      }
      return next
    })
  }, [connectionsQuery.data])

  const createMutation = useMutation({
    mutationFn: (data: ConnectionCreate) =>
      apiFetch<Connection>('/api/v1/connections', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (conn) => {
      queryClient.invalidateQueries({ queryKey: ['connections', 'global'] })
      onAdded(buildBinding(conn))
      setEditorMode('idle'); setSelectedConnectionId(null); resetEditor()
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : t('globalConnectionsTab.errors.failedToSave')
      setEditorError(msg in MCP_ERROR_KEYS ? t(MCP_ERROR_KEYS[msg as keyof typeof MCP_ERROR_KEYS]) : msg)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConnectionUpdate }) =>
      apiFetch<Connection>(`/api/v1/connections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections', 'global'] })
      queryClient.invalidateQueries({ queryKey: ['connections-list'] })
      const { added, deprecated } = diffCountsRef.current
      if (added > 0 && deprecated > 0) {
        setDiscoveryToast(t('globalConnectionsTab.discoverySavedToast.both', { added, deprecated }))
      } else if (added > 0) {
        setDiscoveryToast(t('globalConnectionsTab.discoverySavedToast.added', { count: added }))
      } else if (deprecated > 0) {
        setDiscoveryToast(t('globalConnectionsTab.discoverySavedToast.deprecated', { count: deprecated }))
      }
      setEditorMode('idle'); setSelectedConnectionId(null); resetEditor()
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : t('globalConnectionsTab.errors.failedToSave')
      setEditorError(msg in MCP_ERROR_KEYS ? t(MCP_ERROR_KEYS[msg as keyof typeof MCP_ERROR_KEYS]) : msg)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) =>
      apiFetch<void>(`/api/v1/connections/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['connections', 'global'] })
      queryClient.invalidateQueries({ queryKey: ['connections-list'] })
      onRemoved(id)
      if (selectedConnectionId === id) { setSelectedConnectionId(null); setEditorMode('idle') }
      setDeleteConfirm(null); setDeleteError(null); setDeleteDependents(null)
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409 && err.dependents?.length) {
        const deps = err.dependents as { agentId: string; agentDisplayName: string }[]
        setDeleteDependents(deps)
      } else {
        setDeleteError(err instanceof Error ? err.message : t('globalConnectionsTab.errors.failedToDelete'))
      }
    },
  })

  function resetEditor() {
    setEditorForm(emptyEditorForm); setEditorOps([]); setDiscoveryPattern('StandardTools')
    setOauthAdvancedOpen(false); setDiscoverError(null); setEditorError(null)
    setEditorSnapshot(null)
  }

  function handleEdit(conn: Connection) {
    setSelectedConnectionId(conn.id); setEditorMode('edit')
    const form = buildEditorForm(conn)
    const ops = conn.operations.map(op => ({ ...op, _key: op.id ?? op.key }))
    setEditorForm(form)
    setEditorOps(ops)
    setDiscoveryPattern(conn.operationDiscoveryPattern)
    setOauthAdvancedOpen(false); setDiscoverError(null); setEditorError(null)
    setEditorSnapshot({ form, ops, pattern: conn.operationDiscoveryPattern })
  }

  function handleCancel() { setEditorMode('idle'); setSelectedConnectionId(null); resetEditor() }

  const isEditorDirty = editorSnapshot !== null && (
    JSON.stringify(editorForm) !== JSON.stringify(editorSnapshot.form) ||
    JSON.stringify(editorOps) !== JSON.stringify(editorSnapshot.ops) ||
    discoveryPattern !== editorSnapshot.pattern
  )

  // Entity-editor registry (Connection/Global).
  useEntityEditorRegistration({
    isOpen: () => editorMode !== 'idle',
    isDirty: () => isEditorDirty,
    markClean: () => handleCancel(),
  })

  async function handleCheckHealth(connId: string) {
    setHealthState(prev => ({ ...prev, [connId]: 'checking' }))
    try {
      const result = await apiFetch<ConnectionHealth>(`/api/v1/connections/${connId}/health`)
      setHealthResult(prev => ({ ...prev, [connId]: result }))
      setHealthState(prev => ({ ...prev, [connId]: result.status }))
    } catch {
      setHealthState(prev => ({ ...prev, [connId]: 'unreachable' }))
    }
  }

  async function handleDiscover() {
    setIsDiscovering(true); setDiscoverError(null)
    const prevOpsById = new Map(editorOps.map(o => [o.id, o]))
    try {
      const req: DiscoverRequest = editorForm.endpointUrl
        ? {
            endpointUrl: editorForm.endpointUrl,
            authMethod: editorForm.authMethod,
            operationDiscoveryPattern: discoveryPattern,
            ...(editorMode === 'edit' && selectedConnectionId ? { connectionId: selectedConnectionId } : {}),
          }
        : { connectionId: selectedConnectionId ?? undefined }
      const res = await apiFetch<DiscoverResponse>('/api/v1/connections/discover', { method: 'POST', body: JSON.stringify(req) })
      const discoveredIds = new Set(res.operations.map(op => op.id).filter(Boolean))
      const floatedDeprecated = editorOps
        .filter(op => op.id != null && !discoveredIds.has(op.id) && !op.isDeprecated)
        .map(op => ({ ...op, isDeprecated: true }))
      setEditorOps([
        ...res.operations.map(op => ({
          ...op, _key: op.id ?? op.key,
          isEnabled: op.id != null ? (prevOpsById.get(op.id)?.isEnabled ?? true) : false,
        })) as EditorOperation[],
        ...floatedDeprecated,
      ])
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('globalConnectionsTab.errors.discoveryFailed')
      setDiscoverError(msg in MCP_ERROR_KEYS ? t(MCP_ERROR_KEYS[msg as keyof typeof MCP_ERROR_KEYS]) : msg)
    } finally {
      setIsDiscovering(false)
    }
  }

  function buildAuthConfig(): Record<string, unknown> | null {
    if (editorForm.authMethod !== 'OAuth2') return null
    const cfg: Record<string, unknown> = {}
    if (editorForm.oauthAuthorizationUrl) cfg.authorizationUrl = editorForm.oauthAuthorizationUrl
    if (editorForm.oauthTokenUrl) cfg.tokenUrl = editorForm.oauthTokenUrl
    if (editorForm.oauthClientId) cfg.clientId = editorForm.oauthClientId
    if (editorForm.oauthClientSecret) cfg.clientSecret = editorForm.oauthClientSecret
    return Object.keys(cfg).length > 0 ? cfg : null
  }

  function handleSave() {
    if (!editorForm.name.trim()) { setEditorError(t('globalConnectionsTab.errors.nameRequired')); return }
    if (!editorForm.endpointUrl.trim()) { setEditorError(t('globalConnectionsTab.errors.endpointRequired')); return }
    setSaveConnConfirm(true)
  }

  function executeConnectionSave() {
    setSaveConnConfirm(false)
    const ops = editorOps.filter(op => !op.isDeprecated).map(op => ({
      id: op.id ?? undefined, key: op.key, name: op.name, category: op.category, type: op.type, isEnabled: op.isEnabled,
    }))

    if (editorMode === 'create') {
      createMutation.mutate({
        name: editorForm.name.trim(), description: editorForm.description.trim() || null,
        type: 'MCP', operationDiscoveryPattern: discoveryPattern,
        endpointUrl: editorForm.endpointUrl.trim(), authMethod: editorForm.authMethod,
        authConfig: buildAuthConfig() ?? undefined, operations: ops,
      })
    } else if (editorMode === 'edit' && selectedConnectionId) {
      const prevConn = connections.find(c => c.id === selectedConnectionId)
      diffCountsRef.current = {
        added: editorOps.filter(op => op.id === null && !op.isDeprecated).length,
        deprecated: prevConn ? prevConn.operations.filter(
          op => !op.isDeprecated && !editorOps.some(e => e.id === op.id)
        ).length : 0,
      }
      updateMutation.mutate({ id: selectedConnectionId, data: {
        name: editorForm.name.trim(), description: editorForm.description.trim() || null,
        operationDiscoveryPattern: discoveryPattern, endpointUrl: editorForm.endpointUrl.trim(),
        authMethod: editorForm.authMethod, authConfig: buildAuthConfig() ?? undefined, operations: ops,
      }})
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  function getBinding(connId: string) { return connectionBindings.find(b => b.connectionId === connId) }
  function getSavedBinding(connId: string) { return savedBindings.find(b => b.connectionId === connId) }

  function showConnDependentsHint(connId: string) {
    return getSavedBinding(connId)?.isEnabled === true && getBinding(connId)?.isEnabled === false
  }

  const editingConnCredentialStored =
    editorMode === 'edit' && editorForm.authMethod === 'OAuth2' &&
    connections.find(c => c.id === selectedConnectionId)?.authConfig?.credentialStored === true

  return (
    <Card title={t('globalConnectionsTab.title')} subtitle={t('globalConnectionsTab.subtitle')} className="editor-split-card" noPadding>
      <div className="editor-split-body">

        {/* ── Left: connection list ── */}
        <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>
          {connectionsQuery.isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div>
          )}
          {connectionsQuery.isError && (
            <div style={{ padding: '12px 20px', color: 'var(--danger)', fontSize: 13 }}>{t('globalConnectionsTab.errors.failedToLoad')}</div>
          )}
          {!connectionsQuery.isLoading && connections.length === 0 && (
            <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
              {t('globalConnectionsTab.noConnections')}
            </div>
          )}

          {connections.map(conn => {
            const binding = getBinding(conn.id)
            const isEnabled = binding?.isEnabled ?? true
            const hint = showConnDependentsHint(conn.id)
            const hs = healthState[conn.id] ?? 'idle'
            const isSelected = selectedConnectionId === conn.id && editorMode === 'edit'

            return (
              <div key={conn.id}>
                <div
                  className={`skill-row${isSelected ? ' selected' : ''}`}
                  onClick={() => handleEdit(conn)}
                >
                  <div className="conn-icon icon-mcp"><MCPIcon /></div>

                  <div className="skill-info" style={{ opacity: isEnabled ? 1 : 0.55 }}>
                    <div className="skill-name">{conn.name}</div>
                    {conn.description && <div className="skill-desc">{conn.description}</div>}
                    <ToolTags pattern={conn.operationDiscoveryPattern} />
                  </div>

                  {/* Health result inline label */}
                  {hs === 'checking' && (
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}><Spinner size={12} /></span>
                  )}
                  {hs === 'healthy' && (
                    <span data-testid={`health-result-${conn.id}`} style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', whiteSpace: 'nowrap' }}>
                      {t('globalConnectionsTab.health.healthy')} ({healthResult[conn.id]?.latencyMs ?? 0}ms)
                    </span>
                  )}
                  {hs === 'unreachable' && (
                    <span data-testid={`health-result-${conn.id}`} style={{ fontSize: 11, fontWeight: 600, color: 'var(--danger)', whiteSpace: 'nowrap' }}>
                      {t('globalConnectionsTab.health.unreachable')}
                    </span>
                  )}

                  {/* Pulse dot */}
                  <PulseDot state={hs} />

                  {/* Toggle */}
                  <div onClick={e => e.stopPropagation()}>
                    <Toggle
                      checked={isEnabled}
                      onChange={(v) => { if (!v) setDisableConnConfirm(conn); else onConnectionToggle(conn.id, true) }}
                      label={`Toggle ${conn.name}`}
                    />
                  </div>

                  {/* Health check */}
                  <button type="button" aria-label={`Check health of ${conn.name}`}
                    onClick={e => { e.stopPropagation(); handleCheckHealth(conn.id) }}
                    className="skill-edit-btn" title={t('globalConnectionsTab.checkHealth')}>
                    <HeartbeatIcon />
                  </button>

                  {/* Edit */}
                  <button type="button" aria-label={`Edit ${conn.name}`}
                    onClick={e => { e.stopPropagation(); handleEdit(conn) }}
                    className="skill-edit-btn" style={{ color: 'var(--accent)' }}>
                    <PencilIcon size={14} />
                  </button>

                  {/* Delete — hidden for essential (built-in) connections */}
                  {!conn.isEssential && (
                    <button type="button" aria-label={`Delete ${conn.name}`}
                      onClick={e => { e.stopPropagation(); setDeleteConfirm(conn) }}
                      className="skill-edit-btn" style={{ color: 'var(--danger)' }}>
                      <TrashIcon size={14} />
                    </button>
                  )}
                </div>

                {hint && (
                  <div data-testid={`conn-deps-hint-${conn.id}`}
                    style={{ fontSize: 12, color: 'var(--warning)', padding: '4px 16px 4px 52px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <WarningIcon />
                    {t('globalConnectionsTab.disableWarning')}
                  </div>
                )}
              </div>
            )
          })}

          {deleteError && (
            <div role="alert" style={{ margin: '8px 16px', fontSize: 12, color: 'var(--danger)' }}>{deleteError}</div>
          )}

          {/* Add connection hidden for MVP */}
        </div>

        {/* separator */}
        <div style={{ width: 1, background: 'var(--border-light)', margin: '20px 0' }} />

        {/* ── Right: editor panel ── */}
        <div className="editor-pane-col">

          {editorMode === 'idle' && (
            <div className="editor-pane-empty">
              <LinkIcon />
              <p style={{ fontSize: 13, margin: 0 }}>Select a connection to edit</p>
            </div>
          )}

          {(editorMode === 'create' || editorMode === 'edit') && (
            <div className="editor-form-wrap">
            <div className="editor-form-scroll">
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: 'var(--text-primary)' }}>
                {editorMode === 'create' ? t('globalConnectionsTab.addConnection') : t('globalConnectionsTab.editConnection')}
              </div>
              {editorError && <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{editorError}</div>}

              <div style={{ marginBottom: 16 }}>
                <label htmlFor="gconn-name" style={labelStyle}>{t('globalConnectionsTab.form.nameLabel')}</label>
                <input id="gconn-name" type="text" value={editorForm.name}
                  onChange={e => setEditorForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Mambu MCP" style={inputStyle} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label htmlFor="gconn-type" style={labelStyle}>{t('globalConnectionsTab.form.typeLabel')}</label>
                <select id="gconn-type" value="MCP" disabled style={{ ...inputStyle, opacity: 0.7 }}>
                  <option value="MCP">MCP</option>
                </select>
                <div style={hintStyle}>{t('globalConnectionsTab.form.mcpOnly')}</div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <label htmlFor="gconn-description" style={labelStyle}>
                    {t('globalConnectionsTab.form.descriptionLabel')}
                  </label>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{editorForm.description.length}/150</span>
                </div>
                <input id="gconn-description" type="text" value={editorForm.description}
                  onChange={e => setEditorForm(f => ({ ...f, description: e.target.value.slice(0, 150) }))}
                  maxLength={150}
                  placeholder={t('globalConnectionsTab.shortDescriptionPlaceholder')} style={inputStyle} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label htmlFor="gconn-endpoint" style={labelStyle}>{t('globalConnectionsTab.form.endpointLabel')}</label>
                <input id="gconn-endpoint" type="url" value={editorForm.endpointUrl}
                  onChange={e => setEditorForm(f => ({ ...f, endpointUrl: e.target.value }))}
                  placeholder="https://example.com/mcp" style={inputStyle} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label htmlFor="gconn-auth" style={labelStyle}>{t('globalConnectionsTab.form.authMethodLabel')}</label>
                <select id="gconn-auth" value={editorForm.authMethod}
                  onChange={e => { setEditorForm(f => ({ ...f, authMethod: e.target.value as ConnectionAuthMethod })); setOauthAdvancedOpen(false) }}
                  style={inputStyle}>
                  <option value="None">{t('globalConnectionsTab.form.authNone')}</option>
                  <option value="OAuth2">{t('globalConnectionsTab.form.authOAuth2')}</option>
                </select>
              </div>

              {editorForm.authMethod === 'OAuth2' && (
                <div style={{ marginBottom: 16 }}>
                  <button type="button" onClick={() => setOauthAdvancedOpen(o => !o)}
                    style={{ background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }}>
                    <ChevronRightIcon open={oauthAdvancedOpen} />
                    OAuth2 configuration
                  </button>
                  {oauthAdvancedOpen && (
                    <div style={{ background: 'var(--bg-badge)', borderRadius: 8, padding: 16, marginTop: 8 }}>
                      {[
                        { id: 'gconn-oauth-auth',      label: t('globalConnectionsTab.form.authUrlLabel'),   key: 'oauthAuthorizationUrl' as const, placeholder: 'https://provider.com/oauth/authorize' },
                        { id: 'gconn-oauth-token',     label: t('globalConnectionsTab.form.tokenUrlLabel'),  key: 'oauthTokenUrl' as const,         placeholder: 'https://provider.com/oauth/token' },
                        { id: 'gconn-oauth-client-id', label: t('globalConnectionsTab.form.clientIdLabel'),  key: 'oauthClientId' as const,         placeholder: 'Enter client ID' },
                      ].map(field => (
                        <div key={field.id} style={{ marginBottom: 12 }}>
                          <label htmlFor={field.id} style={{ ...labelStyle, fontSize: 13 }}>{field.label}</label>
                          <input id={field.id} type="text" value={editorForm[field.key]}
                            onChange={e => setEditorForm(f => ({ ...f, [field.key]: e.target.value }))}
                            placeholder={field.placeholder} style={{ ...inputStyle, fontSize: 13 }} />
                        </div>
                      ))}
                      <div>
                        <label htmlFor="gconn-oauth-secret" style={{ ...labelStyle, fontSize: 13 }}>{t('globalConnectionsTab.form.clientSecretLabel')}</label>
                        <input id="gconn-oauth-secret" type="password" value={editorForm.oauthClientSecret}
                          onChange={e => setEditorForm(f => ({ ...f, oauthClientSecret: e.target.value }))}
                          placeholder={editingConnCredentialStored ? t('globalConnectionsTab.form.secretPlaceholder') : 'Enter client secret'}
                          style={{ ...inputStyle, fontSize: 13 }} />
                        {editingConnCredentialStored && !editorForm.oauthClientSecret && (
                          <div style={hintStyle}>{t('globalConnectionsTab.form.secretNote')}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Available Operations ── */}
              <GlobalOpsTree
                ops={editorOps}
                discoveryPattern={discoveryPattern}
                onDiscoveryPatternChange={setDiscoveryPattern}
                onDiscover={handleDiscover}
                isDiscovering={isDiscovering}
                canDiscover={!!editorForm.endpointUrl}
                discoverError={discoverError}
                onOpToggle={(key, enabled) =>
                  setEditorOps(prev => prev.map(o => o._key === key ? { ...o, isEnabled: enabled } : o))
                }
              />

            </div>{/* end editor-form-scroll */}
              <div className="editor-form-bar">
                <button type="button" onClick={handleCancel} style={btnGhostStyle}>{t('globalConnectionsTab.cancel')}</button>
                <button type="button" onClick={handleSave} disabled={isSaving}
                  style={{ ...btnPrimaryStyle, opacity: isSaving ? 0.6 : 1, cursor: isSaving ? 'default' : 'pointer' }}>
                  {isSaving ? <Spinner size={12} /> : null}
                  {t('globalConnectionsTab.saveConnection')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete dialog */}
      {deleteConfirm && (
        <DeleteConnectionDialog
          connection={deleteConfirm}
          dependents={deleteDependents}
          isDeleting={deleteMutation.isPending}
          error={deleteError}
          onConfirm={() => deleteMutation.mutate({ id: deleteConfirm.id, force: deleteDependents !== null })}
          onCancel={() => { setDeleteConfirm(null); setDeleteDependents(null); setDeleteError(null) }}
        />
      )}
      {disableConnConfirm && (
        <ConfirmDialog
          title={t('globalConnectionsTab.disableDialog.title', { name: disableConnConfirm.name })}
          message={t('globalConnectionsTab.disableDialog.message')}
          confirmLabel={t('globalConnectionsTab.disableDialog.confirm')} danger
          onConfirm={() => { onConnectionToggle(disableConnConfirm.id, false); setDisableConnConfirm(null) }}
          onCancel={() => setDisableConnConfirm(null)}
        />
      )}
      {saveConnConfirm && (
        <ConfirmDialog
          title={t('globalConnectionsTab.saveDialog.title')}
          message={t('globalConnectionsTab.saveDialog.message')}
          confirmLabel={t('globalConnectionsTab.saveDialog.confirm')}
          requiresAcknowledgment
          acknowledgmentLabel={t('globalConnectionsTab.saveDialog.acknowledgment')}
          onConfirm={executeConnectionSave}
          onCancel={() => setSaveConnConfirm(false)}
        />
      )}

      {discoveryToast && (
        <div
          role="status"
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 1100,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '10px 16px', boxShadow: '0 4px 16px rgba(0,0,0,.12)',
            display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
          }}
        >
          <span style={{ fontWeight: 600, color: 'var(--accent)' }}>✓</span>
          <span style={{ color: 'var(--text-primary)' }}>{discoveryToast}</span>
          <button
            type="button"
            onClick={() => setDiscoveryToast(null)}
            aria-label={t('globalConnectionsTab.dismiss')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 14, lineHeight: 1, padding: 2 }}
          >✕</button>
        </div>
      )}
    </Card>
  )
}

/* ─── DeleteConnectionDialog ─── */


interface DeleteConnectionDialogProps {
  connection: Connection
  dependents: { agentId: string; agentDisplayName: string }[] | null
  isDeleting: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
}

function DeleteConnectionDialog({ connection, dependents, isDeleting, error, onConfirm, onCancel }: DeleteConnectionDialogProps) {
  const { t } = useTranslation()
  const hasWarning = dependents !== null && dependents.length > 0

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 12, padding: '28px 32px',
        width: 480, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,.2)',
      }}>
        <div style={{ marginBottom: 4, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
          {t('globalConnectionsTab.deleteDialog.title', { name: connection.name })}
        </div>
        {hasWarning && (
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 16 }}>
            {t('globalConnectionsTab.deleteDialog.inUse')}
          </div>
        )}

        {hasWarning ? (
          <>
            <div style={{
              border: '1px solid var(--danger)', borderRadius: 8,
              background: 'var(--danger-bg)', padding: '12px 16px',
              display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16,
            }}>
              <span style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 1 }}><WarningIcon size={18} /></span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)', marginBottom: 4 }}>
                  {t('globalConnectionsTab.deleteDialog.affectsCount', { count: dependents!.length })}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {t('globalConnectionsTab.deleteDialog.willRemoveBelow')}
                </div>
              </div>
            </div>

            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-tertiary)', marginBottom: 8 }}>
              {t('globalConnectionsTab.deleteDialog.affectedSparks')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 20 }}>
              {dependents!.map(d => (
                <div key={d.agentId} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 12px', borderRadius: 6, background: 'var(--bg-badge)',
                  fontSize: 13, color: 'var(--text-primary)',
                }}>
                  <span style={{ color: 'var(--accent)', flexShrink: 0 }}><LightningIcon size={16} /></span>
                  {d.agentDisplayName || d.agentId}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, marginTop: 8 }}>
            {t('globalConnectionsTab.deleteDialog.willRemove')}
          </div>
        )}

        {error && (
          <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} disabled={isDeleting} style={btnGhostStyle}>
            {t('globalConnectionsTab.deleteDialog.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            style={{
              padding: '6px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600,
              cursor: isDeleting ? 'default' : 'pointer',
              background: 'var(--danger)', color: 'var(--text-inverse)', border: '1px solid var(--danger)',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              opacity: isDeleting ? 0.6 : 1, fontFamily: 'inherit',
            }}
          >
            {isDeleting && <Spinner size={12} />}
            {t('globalConnectionsTab.deleteDialog.delete')}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── GlobalOpsTree ─── full ops-tree matching the prototype ─── */

type GTypeFilter   = 'All' | 'Read' | 'Execute'
type GStatusFilter = 'All' | 'Enabled' | 'Disabled'

interface GlobalOpsTreeProps {
  ops: EditorOperation[]
  discoveryPattern: DiscoveryPattern
  onDiscoveryPatternChange: (p: DiscoveryPattern) => void
  onDiscover: () => void
  isDiscovering: boolean
  canDiscover: boolean
  discoverError: string | null
  onOpToggle: (key: string, enabled: boolean) => void
}

function GlobalOpsTree({
  ops,
  discoveryPattern,
  onDiscoveryPatternChange,
  onDiscover,
  isDiscovering,
  canDiscover,
  discoverError,
  onOpToggle,
}: GlobalOpsTreeProps) {
  const { t } = useTranslation()
  const [search, setSearch]               = useState('')
  const [typeFilter, setTypeFilter]       = useState<GTypeFilter>('All')
  const [statusFilter, setStatusFilter]   = useState<GStatusFilter>('All')
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set())
  const [allCollapsed, setAllCollapsed]   = useState(false)

  const visibleOps = ops
  const activeOps  = ops.filter(op => !op.isDeprecated)

  const filteredOps = useMemo(() => visibleOps.filter(op => {
    if (search && !op.name.toLowerCase().includes(search.toLowerCase())) return false
    if (typeFilter !== 'All' && op.type !== typeFilter) return false
    if (statusFilter === 'Enabled'  && (op.isDeprecated || !op.isEnabled)) return false
    if (statusFilter === 'Disabled' && !op.isDeprecated && op.isEnabled) return false
    return true
  }), [visibleOps, search, typeFilter, statusFilter])

  const grouped = useMemo(() => {
    const m = new Map<string, EditorOperation[]>()
    for (const op of filteredOps) {
      const list = m.get(op.category) ?? []; list.push(op); m.set(op.category, list)
    }
    return m
  }, [filteredOps])

  const enabledCount = activeOps.filter(o => o.isEnabled).length

  function applyReadOnly() { activeOps.forEach(op => { if (op.isEnabled !== (op.type==='Read')) onOpToggle(op._key, op.type==='Read') }) }
  function enableShown()   { filteredOps.forEach(op => { if (!op.isDeprecated && !op.isEnabled) onOpToggle(op._key, true)  }) }
  function disableShown()  { filteredOps.forEach(op => { if (!op.isDeprecated &&  op.isEnabled) onOpToggle(op._key, false) }) }

  function toggleCollapseAll() {
    if (allCollapsed) { setCollapsedCats(new Set()); setAllCollapsed(false) }
    else              { setCollapsedCats(new Set([...grouped.keys()])); setAllCollapsed(true) }
  }
  function toggleCat(cat: string) {
    setCollapsedCats(prev => {
      const n = new Set(prev)
      if (n.has(cat)) { n.delete(cat) } else { n.add(cat) }
      return n
    })
  }
  function catEnabledCount(catOps: EditorOperation[]) { return catOps.filter(o => !o.isDeprecated && o.isEnabled).length }
  function catActiveCount(catOps: EditorOperation[])  { return catOps.filter(o => !o.isDeprecated).length }
  function toggleCatOps(catOps: EditorOperation[], val: boolean) { catOps.forEach(op => { if (!op.isDeprecated && op.isEnabled!==val) onOpToggle(op._key, val) }) }

  return (
    <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 16, marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Header row: label + Discovery selector + refresh */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-tertiary)' }}>
          {t('globalConnectionsTab.ops.title')}
        </div>
        <span className="ops-tree-seg-label" style={{ marginLeft: 8 }}>{t('globalConnectionsTab.ops.discovery')}</span>
        <div className="ops-tree-seg">
          {(['StandardTools', 'SearchInvoke'] as DiscoveryPattern[]).map(p => (
            <button key={p} type="button" className={discoveryPattern===p?'on':''} onClick={() => onDiscoveryPatternChange(p)}>
              {p==='SearchInvoke' ? t('globalConnectionsTab.ops.searchInvoke') : t('globalConnectionsTab.ops.standard')}
            </button>
          ))}
        </div>
        <button type="button" aria-label={t('globalConnectionsTab.discoverOperations')} onClick={onDiscover}
          disabled={isDiscovering || !canDiscover}
          style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', padding:4, border:'none', borderRadius:4, background:'none', cursor:(!canDiscover||isDiscovering)?'not-allowed':'pointer', color:'var(--accent)', opacity:(!canDiscover||isDiscovering)?0.4:1 }}>
          {isDiscovering ? <Spinner size={14} /> : <RefreshIcon />}
        </button>
      </div>

      {discoverError && <div role="alert" style={{ fontSize:12, color:'var(--danger)' }}>{discoverError}</div>}

      {ops.length === 0 ? (
        <div className="conn-ops-placeholder"><RefreshIcon /><span>{t('globalConnectionsTab.ops.refreshToLoad')}</span></div>
      ) : (<>

        {/* Filtering */}
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)' }}>{t('globalConnectionsTab.ops.filtering')}</div>
          <div className="ops-tree-toolbar">
            <input type="text" className="ops-tree-search" placeholder="Search operations..."
              value={search} onChange={e => setSearch(e.target.value)} />
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <span className="ops-tree-seg-label">Type</span>
              <div className="ops-tree-seg">
                {(['All','Read','Execute'] as GTypeFilter[]).map(tf => (
                  <button key={tf} className={typeFilter===tf?'on':''} onClick={() => setTypeFilter(tf)}>{tf}</button>
                ))}
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <span className="ops-tree-seg-label">Status</span>
              <div className="ops-tree-seg">
                {(['All','Enabled','Disabled'] as GStatusFilter[]).map(sf => (
                  <button key={sf} className={statusFilter===sf?'on':''} onClick={() => setStatusFilter(sf)}>{sf}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)' }}>{t('globalConnectionsTab.ops.quickActions')}</div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            <button className="ops-tree-action-btn" type="button" onClick={applyReadOnly}>{t('globalConnectionsTab.ops.readOnlyPreset')}</button>
            <button className="ops-tree-action-btn" type="button" onClick={enableShown}>{t('globalConnectionsTab.ops.enableShown')}</button>
            <button className="ops-tree-action-btn" type="button" onClick={disableShown}>{t('globalConnectionsTab.ops.disableShown')}</button>
          </div>
        </div>

        {/* Operations Overview */}
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)' }}>{t('globalConnectionsTab.ops.overview')}</div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span className="ops-tree-count"><strong>{enabledCount}</strong> of {activeOps.length} enabled</span>
              <a role="button" onClick={toggleCollapseAll}
                style={{ fontSize:11, color:'var(--accent)', cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                {allCollapsed ? t('common.expandAll') : t('common.collapseAll2')}
              </a>
            </div>
          </div>

          {filteredOps.length === 0 ? (
            <div className="ops-tree" style={{ padding:16, textAlign:'center', color:'var(--text-tertiary)', fontSize:12 }}>
              {t('globalConnectionsTab.ops.noMatch')}
            </div>
          ) : (() => {
            let leafIdx = 0
            return (
              <div className="ops-tree">
                {[...grouped.entries()].map(([cat, catOps]) => {
                  const isCollapsed = collapsedCats.has(cat)
                  const catEn       = catEnabledCount(catOps)
                  const catActive   = catActiveCount(catOps)
                  const allCatOn    = catActive > 0 && catEn === catActive
                  const isPartial   = catEn > 0 && catEn < catActive
                  return (
                    <div key={cat}>
                      <div className="ops-tree-row ops-tree-parent" style={{ cursor:'pointer' }} onClick={() => toggleCat(cat)}>
                        <span className="ops-tree-caret"><ChevronRightIcon open={!isCollapsed} /></span>
                        <div className="ops-tree-lbl"><span className="t">{cat}</span></div>
                        <span className={`ops-tree-sub${isPartial?' partial':''}`}>{catEn}/{catActive}</span>
                        <div onClick={e => e.stopPropagation()}>
                          <Toggle checked={allCatOn} onChange={v => toggleCatOps(catOps,v)} label={`Toggle all ${cat}`} />
                        </div>
                      </div>
                      {!isCollapsed && catOps.map(op => {
                        const opType  = op.type.toLowerCase() as 'read'|'execute'
                        const isZebra = leafIdx % 2 === 0
                        const isNew   = op.id === null
                        leafIdx++
                        return (
                          <div key={op._key} className={`ops-tree-row leaf${isZebra?' zebra':''}${op.isDeprecated?' deprecated':''}`}>
                            <span className="ops-tree-caret leaf" aria-hidden="true" />
                            <div className="ops-tree-lbl" style={{ paddingLeft:12 }}>
                              <span className="t">{normalizeName(op.name)}</span>
                              <span className={`badge badge-${opType}`}>{op.type}</span>
                              {op.isDeprecated && (
                                <span
                                  className="ops-tree-dep-icon"
                                  title={t('connectionsTab.deprecationWarning')}
                                  style={{ color: 'var(--warning)' }}
                                >
                                  <WarningIcon />
                                </span>
                              )}
                              {isNew && <span className="badge badge-new">New</span>}
                            </div>
                            <Toggle
                              checked={op.isEnabled}
                              onChange={v => !op.isDeprecated && onOpToggle(op._key, v)}
                              label={`Toggle ${op.name} globally`}
                              disabled={op.isDeprecated}
                            />
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            )
          })()}
        </div>
      </>)}
    </div>
  )
}
